/**
 * Revision Manager
 *
 * Core revision tracking logic with IndexedDB-first architecture.
 * Handles revision creation, diff calculation, and history management.
 */

import { v4 as uuidv4 } from 'uuid';
import * as DiffMatchPatch from 'diff-match-patch';
import type {
  Revision,
  RevisionMetadata,
  DiffResult,
  RevisionHistoryFilter,
  RevisionSettings,
  RevisionComparison,
  RevisionExport,
} from './types';
import { IndexedDBRevisionStorage } from './storage/IndexedDBRevisionStorage';

export class RevisionManager {
  private storage: IndexedDBRevisionStorage;
  private diffTool: InstanceType<typeof DiffMatchPatch.diff_match_patch>;
  private settings: RevisionSettings | null = null;
  private debounceTimers = new Map<string, NodeJS.Timeout>();

  constructor() {
    this.storage = new IndexedDBRevisionStorage();
    this.diffTool = new DiffMatchPatch.diff_match_patch();
    this.loadSettings();
  }

  /**
   * Load settings from storage
   */
  private async loadSettings(): Promise<void> {
    this.settings = await this.storage.getSettings();
  }

  /**
   * Create a new revision for a file
   */
  async createRevision(
    fileId: string,
    filePath: string,
    content: string,
    metadata?: Partial<RevisionMetadata>
  ): Promise<Revision> {
    // Load settings if not loaded
    if (!this.settings) {
      await this.loadSettings();
    }

    // Check if auto-revision is enabled
    if (!this.settings!.autoRevision) {
      throw new Error('Auto-revision is disabled');
    }

    // Debounce revision creation
    const debounceKey = `revision-${fileId}`;
    if (this.debounceTimers.has(debounceKey)) {
      clearTimeout(this.debounceTimers.get(debounceKey)!);
    }

    return new Promise((resolve, reject) => {
      const timer = setTimeout(async () => {
        try {
          this.debounceTimers.delete(debounceKey);
          const revision = await this.createRevisionInternal(
            fileId,
            filePath,
            content,
            metadata
          );
          resolve(revision);
        } catch (error) {
          reject(error);
        }
      }, this.settings!.revisionDebounceMs);

      this.debounceTimers.set(debounceKey, timer);
    });
  }

  /**
   * Internal revision creation logic
   */
  private async createRevisionInternal(
    fileId: string,
    filePath: string,
    content: string,
    metadata?: Partial<RevisionMetadata>
  ): Promise<Revision> {
    // Get previous revision
    const previousRevision = await this.storage.getLatestRevision(fileId);

    // Calculate content hash
    const contentHash = await this.calculateHash(content);

    // Check for duplicate content
    if (previousRevision?.contentHash === contentHash) {
      return previousRevision; // No changes, return existing revision
    }

    // Get revision history to determine revision number
    const history = await this.storage.getRevisionHistory({ fileId });
    const revisionNumber = history.length + 1;

    // Determine if this should be a snapshot
    const isSnapshot =
      revisionNumber === 1 ||
      revisionNumber % this.settings!.snapshotInterval === 0 ||
      !this.settings!.enableDeltaCompression;

    // Calculate delta if not a snapshot
    let delta: string | undefined;
    if (!isSnapshot && previousRevision) {
      delta = await this.calculateDelta(previousRevision.content, content);
    }

    // Calculate metadata
    const changeMetadata = await this.calculateChangeMetadata(
      previousRevision?.content,
      content,
      metadata
    );

    // Create revision object
    const revision: Revision = {
      id: uuidv4(),
      fileId,
      filePath,
      content: isSnapshot ? content : '',
      contentHash,
      createdAt: new Date().toISOString(),
      revisionNumber,
      parentRevisionId: previousRevision?.id,
      metadata: changeMetadata,
      size: new TextEncoder().encode(content).length,
      isSnapshot,
      delta: !isSnapshot ? delta : undefined,
    };

    // Store revision
    await this.storage.createRevision(revision);

    // Prune old revisions if needed
    if (this.settings!.maxRevisionsPerFile > 0) {
      await this.storage.pruneOldRevisions(
        fileId,
        this.settings!.maxRevisionsPerFile
      );
    }

    return revision;
  }

  /**
   * Get revision history for a file
   */
  async getRevisionHistory(filter: RevisionHistoryFilter): Promise<Revision[]> {
    const revisions = await this.storage.getRevisionHistory(filter);

    // Reconstruct content for delta revisions
    for (const revision of revisions) {
      if (!revision.isSnapshot && revision.delta) {
        revision.content = await this.reconstructContent(revision);
      }
    }

    return revisions;
  }

  /**
   * Get specific revision content
   */
  async getRevisionContent(revisionId: string): Promise<string | null> {
    const revision = await this.storage.getRevision(revisionId);
    if (!revision) return null;

    if (revision.isSnapshot) {
      return revision.content;
    }

    return this.reconstructContent(revision);
  }

  /**
   * Reconstruct content from delta revision
   */
  private async reconstructContent(revision: Revision): Promise<string> {
    if (revision.isSnapshot) {
      return revision.content;
    }

    // Find the nearest snapshot
    let currentRevision: Revision | null = revision;
    const deltas: string[] = [];

    while (currentRevision && !currentRevision.isSnapshot) {
      if (currentRevision.delta) {
        deltas.unshift(currentRevision.delta);
      }
      if (currentRevision.parentRevisionId) {
        currentRevision = await this.storage.getRevision(
          currentRevision.parentRevisionId
        );
      } else {
        break;
      }
    }

    if (!currentRevision || !currentRevision.isSnapshot) {
      throw new Error('Could not find snapshot for revision reconstruction');
    }

    // Apply deltas to reconstruct content
    let content = currentRevision.content;
    for (const delta of deltas) {
      content = this.applyDelta(content, delta);
    }

    return content;
  }

  /**
   * Generate diff between two revisions
   */
  async generateDiff(
    fromRevisionId: string,
    toRevisionId: string
  ): Promise<DiffResult> {
    const fromRevision = await this.storage.getRevision(fromRevisionId);
    const toRevision = await this.storage.getRevision(toRevisionId);

    if (!fromRevision || !toRevision) {
      throw new Error('One or both revisions not found');
    }

    const fromContent = await this.getRevisionContent(fromRevisionId);
    const toContent = await this.getRevisionContent(toRevisionId);

    if (!fromContent || !toContent) {
      throw new Error('Could not retrieve revision content');
    }

    const diffs = this.diffTool.diff_main(fromContent, toContent);
    this.diffTool.diff_cleanupSemantic(diffs);

    // Calculate statistics
    let additions = 0;
    let deletions = 0;
    const changes: DiffResult['changes'] = [];

    let lineNumber = 1;
    for (const [operation, text] of diffs) {
      const lines = text.split('\n');

      for (const line of lines) {
        if (operation === DiffMatchPatch.DIFF_INSERT) {
          additions++;
          changes.push({
            lineNumber,
            type: 'add',
            content: line,
          });
        } else if (operation === DiffMatchPatch.DIFF_DELETE) {
          deletions++;
          changes.push({
            lineNumber,
            type: 'delete',
            content: line,
          });
        }

        if (operation !== DiffMatchPatch.DIFF_DELETE) {
          lineNumber++;
        }
      }
    }

    // Generate unified diff
    const patches = this.diffTool.patch_make(fromContent, toContent);
    const unifiedDiff = this.diffTool.patch_toText(patches);

    return {
      fromRevision,
      toRevision,
      unifiedDiff,
      additions,
      deletions,
      changes,
    };
  }

  /**
   * Compare two revisions and check for conflicts
   */
  async compareRevisions(
    fileId: string,
    leftRevisionId: string,
    rightRevisionId: string
  ): Promise<RevisionComparison> {
    const leftRevision = await this.storage.getRevision(leftRevisionId);
    const rightRevision = await this.storage.getRevision(rightRevisionId);

    if (!leftRevision || !rightRevision) {
      throw new Error('One or both revisions not found');
    }

    const diff = await this.generateDiff(leftRevisionId, rightRevisionId);

    // Simple conflict detection (can be enhanced)
    const canAutoMerge = diff.changes.every(
      (change) => change.type !== 'modify'
    );

    return {
      fileId,
      leftRevision,
      rightRevision,
      diff,
      canAutoMerge,
    };
  }

  /**
   * Export revision history
   */
  async exportRevisions(
    filter?: RevisionHistoryFilter
  ): Promise<RevisionExport> {
    const revisions = await this.getRevisionHistory(filter || {});

    // Group by file
    const fileRevisions: Record<string, Revision[]> = {};
    for (const revision of revisions) {
      if (!fileRevisions[revision.fileId]) {
        fileRevisions[revision.fileId] = [];
      }
      fileRevisions[revision.fileId].push(revision);
    }

    return {
      version: '1.0.0',
      exportedAt: new Date().toISOString(),
      totalRevisions: revisions.length,
      fileRevisions,
      metadata: {
        includesDeltas: true,
        compressed: false,
        exportRange: filter?.dateRange,
      },
    };
  }

  /**
   * Import revision history
   */
  async importRevisions(exportData: RevisionExport): Promise<void> {
    for (const fileId in exportData.fileRevisions) {
      const revisions = exportData.fileRevisions[fileId];
      for (const revision of revisions) {
        await this.storage.createRevision(revision);
      }
    }
  }

  /**
   * Calculate SHA-256 hash of content
   */
  private async calculateHash(content: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(content);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Calculate delta between two contents
   */
  private async calculateDelta(
    oldContent: string,
    newContent: string
  ): Promise<string> {
    const diffs = this.diffTool.diff_main(oldContent, newContent);
    return JSON.stringify(diffs);
  }

  /**
   * Apply delta to content
   */
  private applyDelta(content: string, delta: string): string {
    const diffs = JSON.parse(delta) as DiffMatchPatch.Diff[];
    const patches = this.diffTool.patch_make(content, diffs);
    const [newContent] = this.diffTool.patch_apply(patches, content);
    return newContent;
  }

  /**
   * Calculate change metadata
   */
  private async calculateChangeMetadata(
    oldContent: string | undefined,
    newContent: string,
    userMetadata?: Partial<RevisionMetadata>
  ): Promise<RevisionMetadata> {
    let linesAdded = 0;
    let linesRemoved = 0;
    let changeType: RevisionMetadata['changeType'] = 'edit';

    if (!oldContent) {
      changeType = 'create';
      linesAdded = newContent.split('\n').length;
    } else {
      const diffs = this.diffTool.diff_main(oldContent, newContent);

      for (const [operation, text] of diffs) {
        const lineCount = text.split('\n').length - 1;
        if (operation === DiffMatchPatch.DIFF_INSERT) {
          linesAdded += lineCount;
        } else if (operation === DiffMatchPatch.DIFF_DELETE) {
          linesRemoved += lineCount;
        }
      }
    }

    return {
      changeType,
      linesAdded,
      linesRemoved,
      userAgent: navigator.userAgent,
      ...userMetadata,
    };
  }

  /**
   * Update revision settings
   */
  async updateSettings(settings: Partial<RevisionSettings>): Promise<void> {
    await this.storage.updateSettings(settings);
    this.settings = await this.storage.getSettings();
  }

  /**
   * Get revision statistics
   */
  async getStatistics() {
    return this.storage.getStatistics();
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    // Clear all debounce timers
    for (const timer of this.debounceTimers.values()) {
      clearTimeout(timer);
    }
    this.debounceTimers.clear();

    // Close storage connection
    this.storage.close();
  }
}
